import { useMemo } from 'react';
import { IconID } from '@hub/common/Icon/Icon';
import { FileExtension } from '@hub/common/hooks/useFileDownload';
import { fileType } from '@hub/bearing-hub/common/constants';
import { IbhShape } from '@igus/iglidur-bearing-hub-api';
import { useProductDownloadAssetsQuery } from '@hub/common/ProductTabs/tabs/DownloadTab/useProductDownloadAssetsQuery';
import { useRouter } from 'next/router';
import { getLocale } from '@hub/libs/constants';
import type { ProductAssetsFunctions } from '@hub/models/graphql';
import { ProductAssetsHandler } from '@hub/models/graphql';
import type { DownloadTabDefinition } from '@hub/bearing-hub/common/downloadTab/DownloadTab';
import { useTranslation } from 'next-i18next';

export const useDownloadTab = (articleNumber: string) => {
  const router = useRouter();
  const locale = getLocale(router);
  const { data: assets } = useProductDownloadAssetsQuery(articleNumber, locale);
  const { t } = useTranslation(['bearing-hub/bearingHub']);

  const staticDownloadOptions = [
    {
      icon: IconID.DOWNLOAD_CAD,
      href: '',
      text: t(`DOWNLOAD_BUTTON.STEP`, { ns: 'bearing-hub/bearingHub' }),
      fileExtension: FileExtension.STP,
      fileLinkType: fileType.stp,
      notSupportedShapes: [],
    },
    {
      icon: IconID.DOWNLOAD_CAD,
      href: '',
      text: t(`DOWNLOAD_BUTTON.STL`, { ns: 'bearing-hub/bearingHub' }),
      fileExtension: FileExtension.STL,
      fileLinkType: fileType.stl,
      notSupportedShapes: [],
    },
    {
      icon: IconID.DOWNLOAD_UNKNOWN,
      href: '',
      text: t(`DOWNLOAD_BUTTON.TECHNICAL_DRAWING`, { ns: 'bearing-hub/bearingHub' }),
      fileExtension: FileExtension.PDF,
      fileLinkType: fileType.drawing,
      notSupportedShapes: [
        IbhShape.FL,
        IbhShape.VFL,
        IbhShape.Y,
        IbhShape.YK,
        IbhShape.TE,
        IbhShape.D,
        IbhShape.VF,
        IbhShape.K,
        IbhShape.VS,
        IbhShape.FSG,
      ],
    },
    {
      icon: IconID.DOWNLOAD_PDF,
      href: '',
      text: t(`DOWNLOAD_BUTTON.PDF_REPORT`, { ns: 'bearing-hub/bearingHub' }),
      fileExtension: FileExtension.PDF,
      fileLinkType: fileType.report,
      notSupportedShapes: [],
    },
  ].filter(download => !!download.text);

  const dynamicDownloadOptions = useMemo(() => {
    if (!assets?.length) {
      return [];
    }

    return assets
      .map(item => ProductAssetsHandler(item))
      .filter((asset): asset is ProductAssetsFunctions => Boolean(asset && asset.getDownloadUrl?.(locale)))
      .map(asset => ({
        icon: IconID.DOWNLOAD_UNKNOWN,
        href: asset.getDownloadUrl(locale) || '',
        text: asset.getDescription(),
        fileExtension: asset.getDownloadContentType(locale),
        notSupportedShapes: [],
      }))
      .filter(item => !!item.text) as DownloadTabDefinition[];
  }, [assets, locale]);

  const downloadTabDefinition = useMemo(
    () => [...staticDownloadOptions, ...dynamicDownloadOptions] as DownloadTabDefinition[],
    [staticDownloadOptions, dynamicDownloadOptions]
  );

  return {
    downloadTabDefinition,
  };
};
